import type {
  ProfitPeriod,
  ProfitAnalysisResult
} from '@/types';

export class ProfitAnalyzer {
  /**
   * 计算单个期间的利润分析
   */
  static analyzePeriod(
    period: ProfitPeriod,
    sellingPrice: number,
    unitBaseCost: number
  ): ProfitAnalysisResult {
    // 计算广告CPA (Cost Per Acquisition)
    const adCpa = period.cpc / (period.cvr / 100);

    // 计算自然订单和广告订单
    const naturalOrders = Math.round(period.targetSales * (period.naturalOrderRate / 100));
    const adOrders = period.targetSales - naturalOrders;
    const adOrderRate = (adOrders / period.targetSales) * 100;

    // 计算收入
    const revenue = period.targetSales * sellingPrice;

    // 计算成本
    const totalBaseCost = period.targetSales * unitBaseCost;
    const totalAdCost = adOrders * adCpa;
    const totalCost = totalBaseCost + totalAdCost;

    // 计算利润
    const profit = revenue - totalCost;
    const netProfitMargin = revenue > 0 ? (profit / revenue) * 100 : 0;

    return {
      period,
      adCpa: this.round(adCpa, 2),
      adOrderRate: this.round(adOrderRate, 1),
      naturalOrders,
      adOrders,
      revenue: this.round(revenue, 0),
      totalBaseCost: this.round(totalBaseCost, 0),
      totalAdCost: this.round(totalAdCost, 0),
      totalCost: this.round(totalCost, 0),
      profit: this.round(profit, 0),
      netProfitMargin: this.round(netProfitMargin, 1)
    };
  }

  /**
   * 分析多个期间
   */
  static analyzeMultiplePeriods(
    periods: ProfitPeriod[],
    sellingPrice: number,
    unitBaseCost: number
  ): ProfitAnalysisResult[] {
    return periods.map(period =>
      this.analyzePeriod(period, sellingPrice, unitBaseCost)
    );
  }

  /**
   * 计算总体统计
   */
  static calculateSummary(results: ProfitAnalysisResult[]): {
    totalRevenue: number;
    totalCost: number;
    totalProfit: number;
    averageMargin: number;
    totalOrders: number;
    totalAdCost: number;
    averageCpa: number;
    averageOrderRate: number;
    profitGrowthRate: number;
  } {
    if (results.length === 0) {
      return {
        totalRevenue: 0,
        totalCost: 0,
        totalProfit: 0,
        averageMargin: 0,
        totalOrders: 0,
        totalAdCost: 0,
        averageCpa: 0,
        averageOrderRate: 0,
        profitGrowthRate: 0
      };
    }

    const totalRevenue = results.reduce((sum, r) => sum + r.revenue, 0);
    const totalCost = results.reduce((sum, r) => sum + r.totalCost, 0);
    const totalProfit = totalRevenue - totalCost;
    const averageMargin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;
    const totalOrders = results.reduce((sum, r) => sum + r.period.targetSales, 0);
    const totalAdCost = results.reduce((sum, r) => sum + r.totalAdCost, 0);
    const averageCpa = results.reduce((sum, r) => sum + r.adCpa, 0) / results.length;
    const averageOrderRate = results.reduce((sum, r) => sum + r.adOrderRate, 0) / results.length;

    // 计算利润增长率
    let profitGrowthRate = 0;
    if (results.length > 1) {
      const firstHalf = results.slice(0, Math.floor(results.length / 2));
      const secondHalf = results.slice(Math.floor(results.length / 2));

      const firstHalfProfit = firstHalf.reduce((sum, r) => sum + r.profit, 0);
      const secondHalfProfit = secondHalf.reduce((sum, r) => sum + r.profit, 0);

      if (firstHalfProfit !== 0) {
        profitGrowthRate = ((secondHalfProfit - firstHalfProfit) / Math.abs(firstHalfProfit)) * 100;
      }
    }

    return {
      totalRevenue: this.round(totalRevenue, 0),
      totalCost: this.round(totalCost, 0),
      totalProfit: this.round(totalProfit, 0),
      averageMargin: this.round(averageMargin, 1),
      totalOrders,
      totalAdCost: this.round(totalAdCost, 0),
      averageCpa: this.round(averageCpa, 2),
      averageOrderRate: this.round(averageOrderRate, 1),
      profitGrowthRate: this.round(profitGrowthRate, 1)
    };
  }

  /**
   * 盈亏平衡分析
   */
  static calculateBreakEven(
    periods: ProfitPeriod[],
    sellingPrice: number,
    unitBaseCost: number
  ): {
    breakEvenSales: number;
    breakEvenPeriod: number;
    breakEvenAdSpend: number;
    monthlyBreakEven: number[];
  } {
    let cumulativeProfit = 0;
    let breakEvenPeriod = -1;
    let breakEvenSales = 0;
    let breakEvenAdSpend = 0;

    const monthlyBreakEven: number[] = [];

    periods.forEach((period, index) => {
      const result = this.analyzePeriod(period, sellingPrice, unitBaseCost);
      cumulativeProfit += result.profit;

      if (breakEvenPeriod === -1 && cumulativeProfit >= 0) {
        breakEvenPeriod = index + 1;
        breakEvenSales = result.period.targetSales;
        breakEvenAdSpend = result.totalAdCost;
      }

      // 计算当月盈亏平衡销量
      const monthlyFixedCost = period.targetSales * unitBaseCost;
      const contributionMargin = sellingPrice - (result.adCpa * (period.cvr / 100));
      const monthlyBreakEvenSales = contributionMargin > 0 ? monthlyFixedCost / contributionMargin : 0;
      monthlyBreakEven.push(Math.ceil(monthlyBreakEvenSales));
    });

    return {
      breakEvenSales,
      breakEvenPeriod,
      breakEvenAdSpend,
      monthlyBreakEven
    };
  }

  /**
   * 敏感性分析
   */
  static performSensitivityAnalysis(
    basePeriod: ProfitPeriod,
    sellingPrice: number,
    unitBaseCost: number
  ): {
    priceSensitivity: Array<{ price: number; profit: number; margin: number }>;
    cpcSensitivity: Array<{ cpc: number; profit: number; margin: number }>;
    cvrSensitivity: Array<{ cvr: number; profit: number; margin: number }>;
  } {
    const baseResult = this.analyzePeriod(basePeriod, sellingPrice, unitBaseCost);

    // 价格敏感性分析 (±20%)
    const priceSensitivity = [];
    for (let priceChange = -20; priceChange <= 20; priceChange += 5) {
      const testPrice = sellingPrice * (1 + priceChange / 100);
      const testResult = this.analyzePeriod(basePeriod, testPrice, unitBaseCost);

      priceSensitivity.push({
        price: this.round(testPrice, 2),
        profit: testResult.profit,
        margin: testResult.netProfitMargin
      });
    }

    // CPC敏感性分析 (±50%)
    const cpcSensitivity = [];
    for (let cpcChange = -50; cpcChange <= 50; cpcChange += 10) {
      const testCpc = basePeriod.cpc * (1 + cpcChange / 100);
      const testPeriod = { ...basePeriod, cpc: testCpc };
      const testResult = this.analyzePeriod(testPeriod, sellingPrice, unitBaseCost);

      cpcSensitivity.push({
        cpc: this.round(testCpc, 2),
        profit: testResult.profit,
        margin: testResult.netProfitMargin
      });
    }

    // CVR敏感性分析 (±50%)
    const cvrSensitivity = [];
    for (let cvrChange = -50; cvrChange <= 50; cvrChange += 10) {
      const testCvr = basePeriod.cvr * (1 + cvrChange / 100);
      const testPeriod = { ...basePeriod, cvr: Math.max(0.1, testCvr) };
      const testResult = this.analyzePeriod(testPeriod, sellingPrice, unitBaseCost);

      cvrSensitivity.push({
        cvr: this.round(testPeriod.cvr, 1),
        profit: testResult.profit,
        margin: testResult.netProfitMargin
      });
    }

    return {
      priceSensitivity,
      cpcSensitivity,
      cvrSensitivity
    };
  }

  /**
   * 最优广告策略建议
   */
  static generateOptimizationSuggestions(
    results: ProfitAnalysisResult[],
    targetMargin: number = 20
  ): Array<{
    type: 'pricing' | 'advertising' | 'conversion';
    priority: 'high' | 'medium' | 'low';
    suggestion: string;
    expectedImprovement: number;
  }> {
    const suggestions = [];
    const summary = this.calculateSummary(results);

    // 利润率分析
    if (summary.averageMargin < targetMargin) {
      suggestions.push({
        type: 'pricing',
        priority: 'high',
        suggestion: `当前平均利润率${summary.averageMargin.toFixed(1)}%低于目标${targetMargin}%，建议提高售价或优化成本结构`,
        expectedImprovement: targetMargin - summary.averageMargin
      });
    }

    // 广告成本分析
    if (summary.averageCpa > summary.totalOrders / results.length * 0.3) {
      suggestions.push({
        type: 'advertising',
        priority: 'high',
        suggestion: `平均CPA $${summary.averageCpa.toFixed(2)}过高，建议优化关键词策略或降低出价`,
        expectedImprovement: summary.averageCpa * 0.2
      });
    }

    // 转化率分析
    const averageCvr = results.reduce((sum, r) => sum + r.period.cvr, 0) / results.length;
    if (averageCvr < 10) {
      suggestions.push({
        type: 'conversion',
        priority: 'medium',
        suggestion: `平均转化率${averageCvr.toFixed(1)}%偏低，建议优化产品页面和广告素材`,
        expectedImprovement: 15 - averageCvr
      });
    }

    // 广告订单比例分析
    if (summary.averageOrderRate > 70) {
      suggestions.push({
        type: 'advertising',
        priority: 'medium',
        suggestion: `广告订单比例${summary.averageOrderRate.toFixed(1)}%过高，建议加强自然流量优化`,
        expectedImprovement: 10
      });
    }

    // 增长趋势分析
    if (summary.profitGrowthRate < 0) {
      suggestions.push({
        type: 'advertising',
        priority: 'high',
        suggestion: `利润呈下降趋势(${summary.profitGrowthRate.toFixed(1)}%)，需要重新评估广告策略`,
        expectedImprovement: Math.abs(summary.profitGrowthRate)
      });
    }

    return suggestions.sort((a, b) => {
      const priorityOrder: Record<string, number> = { high: 3, medium: 2, low: 1 };
      return priorityOrder[b.priority] - priorityOrder[a.priority];
    });
  }

  /**
   * 四舍五入辅助函数
   */
  private static round(value: number, decimals: number): number {
    return Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals);
  }
}